import type { ShopifyCart, CartMutationResponse } from './types/cart';
import {
  CART_CREATE_MUTATION,
  CART_LINES_ADD_MUTATION,
  CART_LINES_UPDATE_MUTATION,
  CART_LINES_REMOVE_MUTATION,
  CART_QUERY,
} from './cart-queries';

const STOREFRONT_URL = `https://${process.env.NEXT_PUBLIC_SHOPIFY_STORE_NAME}.myshopify.com/api/${process.env.NEXT_PUBLIC_SHOPIFY_API_VERSION ?? '2026-04'}/graphql.json`;
const TOKEN = process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_API_TOKEN!;

async function storefrontFetch<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  const res = await fetch(STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!res.ok) {
    throw new Error(`Storefront API ${res.status}`);
  }

  const json = (await res.json()) as { data: T; errors?: unknown[] };

  if (json.errors?.length) {
    throw new Error(`GraphQL: ${JSON.stringify(json.errors)}`);
  }

  return json.data;
}

export async function createCart(
  variantId: string,
  quantity = 1,
  attributes?: Array<{ key: string; value: string }>
): Promise<ShopifyCart> {
  const data = await storefrontFetch<{ cartCreate: CartMutationResponse }>(CART_CREATE_MUTATION, {
    input: {
      lines: [{ merchandiseId: variantId, quantity, ...(attributes && { attributes }) }],
    },
  });

  if (data.cartCreate.userErrors.length > 0) {
    throw new Error(data.cartCreate.userErrors[0].message);
  }

  return data.cartCreate.cart!;
}

export async function addToCart(
  cartId: string,
  variantId: string,
  quantity = 1,
  attributes?: Array<{ key: string; value: string }>
): Promise<ShopifyCart> {
  const data = await storefrontFetch<{ cartLinesAdd: CartMutationResponse }>(
    CART_LINES_ADD_MUTATION,
    { cartId, lines: [{ merchandiseId: variantId, quantity, ...(attributes && { attributes }) }] }
  );

  if (data.cartLinesAdd.userErrors.length > 0) {
    throw new Error(data.cartLinesAdd.userErrors[0].message);
  }

  return data.cartLinesAdd.cart!;
}

export async function updateCartLines(
  cartId: string,
  lineId: string,
  quantity: number
): Promise<ShopifyCart> {
  const data = await storefrontFetch<{
    cartLinesUpdate: CartMutationResponse;
  }>(CART_LINES_UPDATE_MUTATION, {
    cartId,
    lines: [{ id: lineId, quantity }],
  });

  if (data.cartLinesUpdate.userErrors.length > 0) {
    throw new Error(data.cartLinesUpdate.userErrors[0].message);
  }

  return data.cartLinesUpdate.cart!;
}

export async function removeCartLines(cartId: string, lineIds: string[]): Promise<ShopifyCart> {
  const data = await storefrontFetch<{
    cartLinesRemove: CartMutationResponse;
  }>(CART_LINES_REMOVE_MUTATION, { cartId, lineIds });

  if (data.cartLinesRemove.userErrors.length > 0) {
    throw new Error(data.cartLinesRemove.userErrors[0].message);
  }

  return data.cartLinesRemove.cart!;
}

export async function fetchCart(cartId: string): Promise<ShopifyCart | null> {
  try {
    const data = await storefrontFetch<{ cart: ShopifyCart | null }>(CART_QUERY, { cartId });
    return data.cart;
  } catch {
    return null;
  }
}
